/**
 * Behavioral review fixture — conflict-story seed (spec §8.5).
 * Shares the `ReviewSubmission`/`ReviewComment` model owned by `./coding`.
 */

import type { SubmittedReviewRow } from '../../review/contracts'

export const MOCK_BEHAVIORAL_REVIEW: SubmittedReviewRow = {
  id: 'behavioral-conflict-story',
  type: 'behavioral',
  title: '"Tell me about a time you disagreed with a technical decision"',
  chips: ['Recorded + transcribed', 'Conflict / influence'],
  submittedWhen: 'Submitted 5 hours ago',
  sprint: 'Sprint 14',
  // Worked in the older of the two fixture weeks, which is folded by default.
  workedAtMs: Date.parse('2026-08-06T18:00:00.000Z'),
  // Seed: null -> Story Fit's General variant (spec §8.7).
  targetCompany: null,
  ranking: {
    position: 1,
    reason: 'Ranked first — a strong story undercut by its ending and length:',
    chips: [
      {
        icon: '⚠',
        label: 'Weak Result — no metrics',
        tone: 'red',
        detail:
          'The Result beat has no quantified outcome — the highest-leverage fix in the whole answer.',
      },
      {
        icon: '⏱',
        label: 'Ran long: 4:12',
        tone: 'amber',
        detail:
          'A minute past the 2:00–3:30 target band; the Situation alone took 1:34.',
      },
      {
        icon: '?',
        label: 'Asked a question',
        tone: 'blue',
        detail:
          'Post-mortem asks for a rule of thumb on Situation length — waiting on your answer.',
      },
    ],
  },
  // Behavioral's pacing-equivalent is "Delivery" — total = answer length.
  // topicAvg is repurposed as BEHAVIORAL AVG (no subtopic equivalent, §8.5).
  pacing: {
    total: '4:12',
    totalNote: 'Situation ran 1:34',
    companyRows: [
      { company: 'Google', range: '2:00–3:30', verdict: 'Over range' },
    ],
    topicAvg: {
      label: 'BEHAVIORAL AVG',
      value: '3:05',
      delta: '+1:07 vs avg',
      over: true,
    },
  },
  // Coding-only (LG-639); this review type renders no complexity at all.
  complexity: null,
  // Coding-only too (LG-637): no artifact of this kind records executions.
  verification: null,
  postMortem: {
    difficulty: 'Hard',
    answers: [
      {
        id: 'pm-went-well',
        question: 'What went well?',
        answer:
          'The Action section felt organized for once — the spike → data → escalate sequence came out in order without me rambling.',
      },
      {
        id: 'pm-improvement',
        question: 'What could use improvement?',
        answer:
          'I know I spent too long setting the scene again. How much context is actually enough for the Situation — is there a rule of thumb?',
      },
    ],
    // Coding-only: nothing outside the coding editor records an approach.
    approach: null,
    lastEditedWhen: '6 hours ago',
  },
  comments: [
    {
      id: 'behavioral-1',
      kind: 'praise',
      section: 'answer',
      target: 'Answer · Action',
      source: 'ai',
      status: 'approved',
      body: 'The Action beat is textbook influence-without-authority: timeboxed spike → data in the design review → joint escalation with the person you disagreed with. That last move is rare and worth keeping verbatim.',
    },
    {
      id: 'behavioral-2',
      kind: 'critique',
      section: 'answer',
      target: 'Answer · Result',
      source: 'ai',
      status: 'pending',
      body: 'The Result has no numbers — "launch stayed on schedule" is the floor, not the outcome. What did the facade save? Partner integration time, endpoints avoided, incidents prevented? One metric makes this a Staff-level story.',
      alt: 'Strong story, soft landing: close with one number. Even "the facade covered 3 endpoints instead of migrating 40" quantifies the judgment call and makes the Result stick.',
    },
    {
      id: 'behavioral-3',
      kind: 'critique',
      section: 'delivery',
      target: 'Delivery · Length',
      source: 'ai',
      status: 'approved',
      body: '4:12 is a minute past where interviewers start steering. The Situation alone ran 1:34 — cut it to ~40 seconds (team, deadline, the disagreement) and the whole answer lands inside 3:15.',
    },
    {
      id: 'behavioral-4',
      kind: 'suggestion',
      section: 'postmortem',
      target: 'Post-mortem · Question',
      source: 'ai',
      status: 'pending',
      body: 'They asked for a Situation rule of thumb — suggested answer: 3 sentences max (context, stakes, tension), or ~30–45 seconds. If the interviewer needs more, they’ll ask — that’s a better signal than front-loading.',
      alt: 'Direct answer to their question: Situation = 3 sentences (context, stakes, tension). Anything the interviewer needs beyond that, they’ll ask for — questions are engagement, not failure.',
    },
  ],
}
